import React, { memo, useContext } from 'react';
import { SearchModes } from '@/domainLogic/index.ts';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { SearchPageContext } from '@/app/components/search/components/SearchPageContainer/SearchPageContext.ts';
import { getSearchParamsClient } from '@/app/components/search/common/getSearchParams.ts';
import { useSearchDefaultData } from '@/app/components/search/searchDefaultDataProvider.tsx';
import { useSearchDealsPageData } from '@/app/components/search/searchDealsPageProvider.tsx';
import { MaskIcon } from '@/app/common/icons/MaskIcon';
import { HeadingText, ViewModeButton, HeadingRow } from './styled.ts';
import getTitleProps from '../utils';
import type { LayoutVariant } from './types';

const ViewMapIcon = ({ size = 20 }: { size?: number }) => <MaskIcon category="Location" name="icon-map" size={size} />;

export const ResultsHeadingBar = memo(({ isWaiting, variant }: { isWaiting: boolean; variant?: LayoutVariant }) => {
  const { searchParams: searchParamsServer, searchInfoData, searchInfoDetailData, localization } = usePageWrapperData();
  const { defaultValues } = useSearchDefaultData();
  const { productsData, isLoading } = useSearchPageData();
  const { isMobile } = useCommonClient();
  const { searchMode, isLiveSearch } = searchInfoData;
  const { accData } = productsData;
  const { t } = useTranslation();
  const { listView } = useContext(SearchPageContext);
  const searchParams = getSearchParamsClient(searchParamsServer);
  const dealsCount = productsData?.productCounters?.productsCount || 0;
  const { defaultDates: searchDates } = defaultValues;
  const isDP = searchMode === SearchModes.DP;
  const showCustomCheckinCheckoutControl =
    localization?.configValuesApp?.customCheckinCheckoutEnabled &&
    isDP &&
    isLiveSearch &&
    localization?.configValuesApp?.debugEnabled;
  // listView=true means we're in list mode → the button should say "View Map"
  const viewMap = !!listView;
  const { handleViewModeSwitch } = useSearchDealsPageData();

  const { text } = getTitleProps({
    searchParams,
    searchMode,
    searchInfoDetailData,
    searchDates,
    dealsCount,
    localization,
    isMobile,
    hasPreselectedFlight: false,
    showCustomCheckinCheckout: showCustomCheckinCheckoutControl,
    accData,
    t,
  });

  return (
    <HeadingRow variant={variant}>
      <HeadingText isWaiting={isWaiting}>{text}</HeadingText>
      <ViewModeButton
        appearance="outlined"
        onClick={handleViewModeSwitch}
        iconLeading={viewMap ? ViewMapIcon : undefined}
        disabled={isWaiting || isLoading}
      >
        {viewMap ? t('vacation.oss.map.view_map') : t('vacation.oss.map.view_list')}
      </ViewModeButton>
    </HeadingRow>
  );
});

ResultsHeadingBar.displayName = 'ResultsHeadingBar';
